import React, { useId, useState } from 'react';
import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TermsModal } from '@components/Modals/TermsModal';

export interface TermsOfServiceAgreementRenderProps {
  checkboxProps: InputHTMLAttributes<HTMLInputElement>;
  openModal: () => void;
  setChecked: (nextValue: boolean) => void;
  renderToggleControl: () => React.ReactNode;
  labelText: ReactNode;
  linkText?: ReactNode;
}

export interface TermsOfServiceAgreementProps {
  id?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  renderTrigger?: (openModal: () => void) => React.ReactNode;
  checkboxProps?: Omit<InputHTMLAttributes<HTMLInputElement>, 'checked' | 'onChange' | 'id' | 'type'>;
  children?: (props: TermsOfServiceAgreementRenderProps) => React.ReactNode;
  hideInlineLink?: boolean;
  labelText?: ReactNode;
  linkText?: ReactNode;
  labelClassName?: string;
  linkClassName?: string;
  modalTitle?: string;
  acceptText?: string;
  declineText?: string;
}

export function TermsOfServiceAgreement({
  id,
  checked,
  onChange,
  renderTrigger,
  checkboxProps,
  children,
  hideInlineLink,
  labelText: labelTextOverride,
  linkText: linkTextOverride,
  labelClassName,
  linkClassName,
  modalTitle: modalTitleOverride,
  acceptText,
  declineText,
}: TermsOfServiceAgreementProps) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const generatedId = useId();
  const checkboxId = id ?? generatedId;

  const setChecked = (nextValue: boolean) => {
    onChange(nextValue);
  };

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const {
    className: checkboxClassNameFromProps,
    style: checkboxStyleFromProps,
    onFocus: checkboxOnFocus,
    onBlur: checkboxOnBlur,
    ...restCheckboxProps
  } = checkboxProps ?? {};

  const labelStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.75rem',
    fontSize: '0.9rem',
  };

  const textStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    lineHeight: 1.4,
  };

  const linkButtonStyle: CSSProperties = {
    background: 'transparent',
    border: 'none',
    padding: 0,
    color: 'var(--mantine-color-blue-6)',
    cursor: 'pointer',
    font: 'inherit',
    textDecoration: 'underline',
  };

  const controlStyle: CSSProperties = {
    position: 'relative',
    width: '1.25rem',
    height: '1.25rem',
    flexShrink: 0,
    display: 'inline-flex',
  };

  const checkboxStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    cursor: 'pointer',
    accentColor: 'var(--mantine-color-blue-6)',
    ...checkboxStyleFromProps,
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setHasFocus(true);
    checkboxOnFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setHasFocus(false);
    checkboxOnBlur?.(event);
  };

  const checkboxAttributes: InputHTMLAttributes<HTMLInputElement> = {
    id: checkboxId,
    type: 'checkbox',
    checked,
    onChange: handleCheckboxChange,
    className: checkboxClassNameFromProps,
    style: checkboxStyle,
    onFocus: handleFocus,
    onBlur: handleBlur,
    ...restCheckboxProps,
  };

  const renderToggleControl = () => (
    <span style={controlStyle}>
      <input {...checkboxAttributes} />
    </span>
  );

  const labelText = labelTextOverride ?? (hideInlineLink
    ? t('terms.agreement.full', 'I accept the Terms of Service')
    : t('terms.agreement.prefix', 'I accept the'));
  const linkText = linkTextOverride ?? (hideInlineLink ? undefined : t('terms.agreement.link', 'Terms of Service'));
  const modalTitle = modalTitleOverride ?? t('terms.modal.title', 'Terms of Service');
  const acceptCopy = acceptText ?? t('terms.modal.accept', 'Accept');
  const declineCopy = declineText ?? t('terms.modal.decline', 'Decline');

  const defaultAgreement = (
    <label htmlFor={checkboxId} style={labelStyle} className={labelClassName}>
      {renderToggleControl()}
      <span style={textStyle}>
        {labelText}
        {linkText ? (
          <>
            {' '}
            <button type="button" style={linkButtonStyle} className={linkClassName} onClick={openModal}>
              {linkText}
            </button>
          </>
        ) : null}
      </span>
    </label>
  );

  return (
    <>
      {renderTrigger?.(openModal)}
      {children
        ? children({
            checkboxProps: checkboxAttributes,
            openModal,
            labelText,
            linkText,
            setChecked,
            renderToggleControl,
          })
        : defaultAgreement}
      <TermsModal
        isOpen={showModal}
        onClose={closeModal}
        onAccept={() => setChecked(true)}
        onDecline={() => setChecked(false)}
        title={modalTitle}
        acceptText={acceptCopy}
        declineText={declineCopy}
      />
    </>
  );
}
